import React from 'react';
import type { AppRoutePath } from '@shared/core/router.helper/useRouterHelper';
import { RoutesProvider } from '@shared/core';
import { CREATE_WEDDING_APP_NAME } from '@apps/acquisitionFunnels/createWedding/constants';
import { CREATE_BABY_REGISTRY_APP_NAME } from '@apps/acquisitionFunnels/createBabyRegistry/constants';
import { SERVICE_CENTER_APP_NAME } from '@apps/card/serviceCenter/constants';
import { EventRoute } from './EventRoute';
import { getEcardType } from '@apps/admin/saveTheDate/SaveTheDate.utils';
import EcardError from '@shared/components/EmailsAndEcards/components/Error';
import { SAVE_THE_DATE_PHOTO_ECARD_TYPE } from '@apps/admin/saveTheDate/constants';
import { CardsRoutesProvider } from '@apps/card/Card.routes';
import { LazyLogoutPage } from '@apps/admin/logout/LogoutPage.lazy';

// Shared loadables (used in both CSR and SSR)
import { LazyAdmin } from '@apps/admin/Admin.lazy';
import { LazyGuest } from '@apps/guest/Guest.lazy';
import { LazyAccount } from '@apps/account/Account.lazy';
import { LazyShop } from '@apps/registry/public/routes/Shop/Shop.lazy';
import { LazyAccountMe } from '@apps/admin/accountMe/AccountMe.lazy';
import { LazyCreateWedding } from '@apps/acquisitionFunnels/createWedding/CreateWedding.lazy';
import { LazyCreateBabyRegistry } from '@apps/acquisitionFunnels/createBabyRegistry/CreateBabyRegistry.lazy';
import { LazyEventRedirect } from '@apps/admin/routes/EventRedirect/EventRedirect.lazy';
import { LazyEcardGuest } from '@apps/ecard/guest/EcardGuest.lazy';
import { LazySaveTheDateGuest } from '@apps/admin/saveTheDate/guest/SaveTheDateGuest.lazy';
import { LazyServiceCenter } from '@apps/card/serviceCenter/ServiceCenter.lazy';
import { LazyWebsiteDesignTool } from '@apps/admin/websiteDesignTool/WebsiteDesignTool.lazy';
import { WEBSITE_DESIGN_TOOL_APP_NAME } from '@apps/admin/websiteDesignTool/constants';
import { LazyPublicShop } from '@apps/card/routes/PublicShop/PublicShop.lazy';
import { LazyCreateGeneralEvent } from '@apps/acquisitionFunnels/createGeneralEvent/CreateGeneralEvent.lazy';
import { LazyPublicStays } from '@apps/stays/PublicStays/PublicStays.lazy';
import { LazyPublicHoneymoon } from '@apps/stays/PublicHoneymoon/PublicHoneymoon.lazy';
import { LazyRegistryGuest } from '@apps/registry/guest/RegistryGuest.lazy';

// SSR-only loadables
import { LazyContactCollectorGuest } from '@apps/guest/contactCollector/ContactCollectorGuest.lazy';
import { LazyRecipientPremiumEcardView } from '@apps/card/routes/RecipientPremiumEcardView/RecipientPremiumEcardView.lazy';
import { LazyPageNotFound } from '@shared/components/PageNotFound/ssr/PageNotFoundSSR.lazy';

const ShopComponent = () => {
  return (
    <RoutesProvider>
      <LazyShop />
    </RoutesProvider>
  );
};

const ContactCollectorGuestWrapper = ({ magicLinkId }: { magicLinkId: string }) => {
  return (
    <RoutesProvider>
      <LazyContactCollectorGuest magicLinkId={magicLinkId} />
    </RoutesProvider>
  );
};

export const ssrRoutes: Array<AppRoutePath<{ eventHandle: string; magicLinkId: string; ecardId: string }>> = [
  {
    isGuestFacing: false,
    path: '/404',
    component: () => <LazyPageNotFound />,
  },
  {
    isGuestFacing: true,
    path: '/contact/:magicLinkId',
    component: ({ match }) => (match ? <ContactCollectorGuestWrapper magicLinkId={match.params.magicLinkId} /> : null),
  },
  {
    isGuestFacing: false,
    path: '/account/(events|personal-info)',
    component: () => <LazyAccountMe appUrl={'account'} />,
  },
  {
    isGuestFacing: false,
    path: '/account',
    component: () => <LazyAccount appUrl="account" />,
  },
  {
    isGuestFacing: false,
    path: '/shipping-policy',
    // This route will only render if the feature flag is provided
    component: () => <ShopComponent />,
  },
  {
    isGuestFacing: false,
    path: '/return-policy',
    // This route will only render if the feature flag is provided
    component: () => <ShopComponent />,
  },
  {
    isGuestFacing: false,
    path: '/card/:ecardId(\\w{10})',
    component: ({ match }) => (
      <CardsRoutesProvider>
        <LazyRecipientPremiumEcardView orderNumber={match.params.ecardId} />
      </CardsRoutesProvider>
    ),
  },
  {
    isGuestFacing: false,
    path: '/cards',
    component: () => <LazyPublicShop />,
  },
  {
    isGuestFacing: false,
    path: '/shop',
    // This route will only render if the feature flag is provided
    component: () => <ShopComponent />,
  },
  {
    isGuestFacing: false,
    path: `/${CREATE_WEDDING_APP_NAME}`,
    component: () => <LazyCreateWedding />,
  },
  {
    isGuestFacing: false,
    path: `/${CREATE_BABY_REGISTRY_APP_NAME}`,
    component: () => <LazyCreateBabyRegistry />,
  },
  {
    isGuestFacing: true,
    path: '/ecard',
    component: ({ location }) => {
      const ecardType = getEcardType(location);
      return ecardType === undefined ? <EcardError /> : ecardType === SAVE_THE_DATE_PHOTO_ECARD_TYPE ? <LazySaveTheDateGuest /> : <LazyEcardGuest />;
    },
  },
  {
    isGuestFacing: false,
    path: '/createevent',
    component: () => <LazyCreateGeneralEvent />,
  },
  {
    isGuestFacing: false,
    path: '/event',
    // second agent
    exact: true,
    component: () => <LazyEventRedirect />,
  },
  {
    isGuestFacing: false,
    path: '/event/edit',
    component: () => <LazyEventRedirect />,
  },
  {
    isGuestFacing: false,
    path: '/travel',
    component: () => <LazyPublicStays />,
  },
  // Above the `/:eventHandle` catch-all further down, which would otherwise read "honeymoon" as
  // someone's wedding-site handle.
  {
    isGuestFacing: false,
    path: '/honeymoon',
    component: () => <LazyPublicHoneymoon />,
  },
  {
    // Must precede the /servicecenter route: it is a non-exact match that would otherwise shadow this nested path
    isGuestFacing: false,
    path: `/${WEBSITE_DESIGN_TOOL_APP_NAME}`,
    component: () => <LazyWebsiteDesignTool />,
  },
  {
    isGuestFacing: false,
    path: `/${SERVICE_CENTER_APP_NAME}`,
    component: () => <LazyServiceCenter />,
  },
  {
    isGuestFacing: false,
    path: '/logout',
    component: () => <LazyLogoutPage />,
  },
  {
    isGuestFacing: false,
    path: '/:eventHandle/edit',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={LazyAdmin}
      />
    ),
  },
  {
    isGuestFacing: true,
    path: '/:eventHandle/registry',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={LazyRegistryGuest}
      />
    ),
  },
  {
    isGuestFacing: true,
    path: '/:eventHandle',
    component: ({ match }) => (
      <EventRoute eventHandle={match.params.eventHandle}>
        <LazyGuest />
      </EventRoute>
    ),
  },
];
